type CachedEntry = {
    value: any;
    expiresAt: number;
};

const TTL_MS = 12000;
// When backend returns 0 rows, cache for 60s to prevent retry storms
const EMPTY_CACHE_TTL_MS = 60000;
const cache = new Map<string, CachedEntry>();
const inflight = new Map<string, Promise<any>>();

// ── Partial-data background polling ─────────────────────────────────────────
// Backend (/api/accumulated-data) may return `partial: true` while a multi-
// expiration stable fetch (weekly / monthly / 6-month / annual — any range the
// user picks in StableDataFetchBar) is still backfilling the remaining
// expirations in the background. The polling logic is range-agnostic: it keys
// off the backend's `partial` flag, not the requested expiry window. We surface
// the partial dataset immediately, then transparently poll until the dataset
// completes (or the attempt budget is exhausted).

/** Polls every ~10s while partial. */
export const STABLE_PARTIAL_POLL_INTERVAL_MS = 10_000;
/**
 * Max consecutive polls WITHOUT a new expiry before we give up and surface
 * "data may be incomplete" (progress resets the count, so long ranges keep polling).
 * Budget accommodates the longest supported window (annual / 1-year), whose
 * backfill can run roughly 2× longer than the 6-month case (~30–60s → up to
 * ~120s). 12 × 10s = ~120s of headroom; tune down if you want stricter limits.
 */
export const STABLE_PARTIAL_MAX_ATTEMPTS = 12;

export interface StablePartialState {
    /** Full request URL being polled. */
    url: string;
    /** Best-effort underlying symbol extracted from `?underlyings=` (for display). */
    symbol: string | null;
    /** True while the backend still reports partial data. */
    partial: boolean;
    /** Backend-provided message (e.g. "Data loaded; remaining loading in background"). */
    message: string | null;
    /** 0 = initial response, 1..N = subsequent polls. */
    attempt: number;
    /** Same as STABLE_PARTIAL_MAX_ATTEMPTS for display math. */
    maxAttempts: number;
    /** True once the attempt budget was exhausted without `partial: false`. */
    exhausted: boolean;
    /** Epoch ms of last state change (used to age out terminal states). */
    updatedAt: number;
}

type PartialListener = (entries: StablePartialState[]) => void;

const partialStateByUrl = new Map<string, StablePartialState>();
const listeners = new Set<PartialListener>();
const inflightPolls = new Map<string, Promise<void>>();

/**
 * Inject `fields=lite` into stable accumulated-data URLs. Lite skips OHLC +
 * second-order Theta calls (3 calls instead of 5), keeping response times fast.
 * The warm-DB pre-computes Volume from OHLC enrichment where available.
 *
 * Applies only when the URL doesn't already specify a `fields=` param.
 * Exported so direct-fetch callers can normalise the same way.
 */
export function ensureFieldsLite(url: string): string {
    if (!/\/api\/accumulated-data/.test(url)) return url;
    if (!/[?&]source=stable(&|$)/.test(url)) return url;
    let out = url;
    if (!/[?&]fields=/.test(out)) {
        const sep = out.includes("?") ? "&" : "?";
        out = `${out}${sep}fields=lite`;
    }
    return withSnapshotDate(out);
}

/**
 * Add the "as of" date the user picked, if any.
 *
 * Separate from the fields choice, and exported, because every path that requests a stable
 * chain needs it and they do not agree on fields. It was previously folded into
 * `ensureFieldsLite`, so the only way to send an as-of was to also force `fields=lite` --
 * which omits the OHLC snapshot and renders Volume as zero. The Fetch button's own path
 * called neither, so the control did nothing at all from there.
 */
export function withSnapshotDate(url: string): string {
    if (!/\/api\/accumulated-data/.test(url)) return url;
    if (/[?&]snapshot_date=/.test(url)) return url;
    if (typeof localStorage === "undefined") return url;
    try {
        const sd = localStorage.getItem("mmec_snapshot_date");
        if (!sd) return url;
        const sep = url.includes("?") ? "&" : "?";
        return `${url}${sep}snapshot_date=${encodeURIComponent(sd)}`;
    } catch {
        return url;
    }
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(new DOMException("Aborted", "AbortError"));
            return;
        }
        const t = setTimeout(() => {
            signal?.removeEventListener("abort", onAbort);
            resolve();
        }, ms);
        const onAbort = () => {
            clearTimeout(t);
            reject(new DOMException("Aborted", "AbortError"));
        };
        signal?.addEventListener("abort", onAbort, { once: true });
    });
}

function emit() {
    if (listeners.size === 0) return;
    const snapshot = Array.from(partialStateByUrl.values());
    listeners.forEach((l) => {
        try {
            l(snapshot);
        } catch {
            // listener errors must not break the emitter
        }
    });
}

function symbolFromUrl(url: string): string | null {
    try {
        const m = url.match(/[?&]underlyings=([^&]+)/);
        if (!m) return null;
        return decodeURIComponent(m[1]);
    } catch {
        return null;
    }
}

function setPartialState(state: StablePartialState) {
    partialStateByUrl.set(state.url, state);
    emit();
}

function removePartialState(url: string) {
    if (partialStateByUrl.delete(url)) emit();
}

/**
 * Subscribe to the live partial-status feed. The listener is invoked
 * immediately with the current snapshot, and again whenever any URL's state
 * changes. Returns an unsubscribe function.
 */
export function subscribeStablePartialStatus(
    listener: PartialListener,
): () => void {
    listeners.add(listener);
    try {
        listener(Array.from(partialStateByUrl.values()));
    } catch {
        // ignore
    }
    return () => {
        listeners.delete(listener);
    };
}

/**
 * Snapshot of the current partial-status entries (useful for non-React callers).
 */
export function getStablePartialStatus(): StablePartialState[] {
    return Array.from(partialStateByUrl.values());
}

/**
 * Clear a terminal/exhausted entry manually (e.g. user dismisses the banner).
 */
export function clearStablePartialStatus(url: string): void {
    removePartialState(url);
}

/**
 * Background poll loop for a URL whose response was `partial: true`.
 * Bypasses the request cache (via cache-busting `_` param) so each poll hits
 * the backend, updates the cache with the freshest data, and emits status
 * changes. Stops when backend reports `partial: false` or attempts run out.
 *
 * Fire-and-forget: never throws to the caller.
 */
function pollUntilComplete(url: string, signal?: AbortSignal): Promise<void> {
    const existing = inflightPolls.get(url);
    if (existing) return existing;

    const symbol = symbolFromUrl(url);

    const promise = (async () => {
        // The budget counts polls WITHOUT progress, not polls: a wide range keeps loading
        // expiries for minutes and is only abandoned once it stops gaining any.
        let idle = 0;
        let lastLoaded = -1;
        for (let attempt = 1; idle < STABLE_PARTIAL_MAX_ATTEMPTS; attempt++) {
            idle += 1;
            try {
                await delay(STABLE_PARTIAL_POLL_INTERVAL_MS, signal);
            } catch {
                return; // aborted
            }

            // Stop if some other path already cleared partial state for this URL.
            const cur = partialStateByUrl.get(url);
            if (!cur || (!cur.partial && !cur.exhausted)) return;

            let json: any = null;
            try {
                const sep = url.includes("?") ? "&" : "?";
                // cache-buster so we hit the backend, not the local cache
                const res = await fetch(`${url}${sep}_=${Date.now()}`);
                if (res.ok) json = await res.json();
            } catch {
                // network error: try again next cycle
                setPartialState({
                    ...cur,
                    attempt,
                    updatedAt: Date.now(),
                });
                continue;
            }

            if (!json) {
                setPartialState({ ...cur, attempt, updatedAt: Date.now() });
                continue;
            }

            // Refresh the read-through cache with whatever we got (likely more rows).
            const dataLen = Array.isArray(json?.data) ? json.data.length : 0;
            const ttl = dataLen === 0 ? EMPTY_CACHE_TTL_MS : TTL_MS;
            cache.set(url, { value: json, expiresAt: Date.now() + ttl });

            const loadedNow = Number(json?.expiries_loaded);
            if (Number.isFinite(loadedNow) && loadedNow > lastLoaded) {
                if (lastLoaded >= 0) idle = 0;
                lastLoaded = loadedNow;
            }
            const stillPartial = json?.partial === true;
            const message =
                typeof json?.message === "string" ? json.message : cur.message;

            if (!stillPartial) {
                // Done — keep a brief terminal record so the bar can flash "complete".
                setPartialState({
                    url,
                    symbol,
                    partial: false,
                    message,
                    attempt,
                    maxAttempts: STABLE_PARTIAL_MAX_ATTEMPTS,
                    exhausted: false,
                    updatedAt: Date.now(),
                });
                setTimeout(() => removePartialState(url), 1500);
                return;
            }

            setPartialState({
                url,
                symbol,
                partial: true,
                message,
                attempt,
                maxAttempts: STABLE_PARTIAL_MAX_ATTEMPTS,
                exhausted: false,
                updatedAt: Date.now(),
            });
        }

        // Attempt budget exhausted — surface a soft warning, keep last data.
        const cur = partialStateByUrl.get(url);
        if (cur && (cur.partial || !cur.exhausted)) {
            setPartialState({ ...cur, exhausted: true, updatedAt: Date.now() });
            // Auto-clear the exhausted warning after 30s so it doesn't linger.
            setTimeout(() => removePartialState(url), 30_000);
        }
    })();

    inflightPolls.set(url, promise);
    promise.finally(() => {
        if (inflightPolls.get(url) === promise) inflightPolls.delete(url);
    });
    return promise;
}

function withAbort<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
    if (!signal) return promise;
    if (signal.aborted) {
        return Promise.reject(
            new DOMException("The operation was aborted.", "AbortError"),
        );
    }
    return new Promise<T>((resolve, reject) => {
        const onAbort = () =>
            reject(
                new DOMException("The operation was aborted.", "AbortError"),
            );
        signal.addEventListener("abort", onAbort, { once: true });
        promise.then(
            (value) => {
                signal.removeEventListener("abort", onAbort);
                resolve(value);
            },
            (error) => {
                signal.removeEventListener("abort", onAbort);
                reject(error);
            },
        );
    });
}

export async function fetchStableAccumulatedJson(
    url: string,
    signal?: AbortSignal,
): Promise<any> {
    // Fast path: inject fields=lite for the warm-DB backend.
    url = ensureFieldsLite(url);
    const now = Date.now();
    const cached = cache.get(url);
    if (cached && cached.expiresAt > now) return cached.value;

    const existing = inflight.get(url);
    if (existing) return withAbort(existing, signal);

    const request = (async () => {
        const res = await fetch(url);
        const json = await res.json();
        const dataLen = Array.isArray(json?.data) ? json.data.length : 0;
        // Cache empty responses for 60s to prevent retry storms on symbols with no data
        const ttl = dataLen === 0 ? EMPTY_CACHE_TTL_MS : TTL_MS;
        cache.set(url, { value: json, expiresAt: Date.now() + ttl });

        // Partial-data orchestration: backend signals `partial: true` while the
        // remaining 6-month expirations are still being backfilled. We surface
        // the partial dataset now and poll in the background until complete.
        const isPartial = json?.partial === true;
        if (isPartial) {
            const message =
                typeof json?.message === "string" ? json.message : null;
            // Only seed state if not already polling (avoid clobbering higher attempt counts)
            if (!partialStateByUrl.has(url)) {
                setPartialState({
                    url,
                    symbol: symbolFromUrl(url),
                    partial: true,
                    message,
                    attempt: 0,
                    maxAttempts: STABLE_PARTIAL_MAX_ATTEMPTS,
                    exhausted: false,
                    updatedAt: Date.now(),
                });
            }
            // Fire-and-forget; failures are logged inside the loop and never throw here.
            pollUntilComplete(url).catch(() => {});
        } else if (partialStateByUrl.has(url)) {
            // Backend now reports complete for a URL we were polling — clean up.
            removePartialState(url);
        }

        return json;
    })();

    inflight.set(url, request);
    try {
        return await withAbort(request, signal);
    } finally {
        inflight.delete(url);
    }
}
